import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import CourseWizard from "../Course/CourseWizard";
import { useCourseDetailV2 } from "../Course/hooks/useCourseDetailV2";
import { useEditCourseV2 } from "../Course/hooks/useEditCourseV2";
import { useSubmitCourseV2 } from "../Course/hooks/useSubmitCourseV2";
import { CourseWizardData } from "../Course/types";
import { useSaveDraft } from "../hooks/useSaveDraft";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import { usePromoteDraft } from "../hooks/usePromoteDraft";
import {
  DraftErrorInfo,
  extractPromoteError,
} from "../hooks/parsePromoteErrors";
import DraftPromoteErrorDialog from "../components/DraftPromoteErrorDialog";
import { flattenSelectedLOItems } from "../utils/loStructure";

interface EditCourseProps {
  duplication?: boolean;
}

const EditCourse: React.FC<EditCourseProps> = ({ duplication = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("wizard-course");
  const { id: corporateId = "", idCourse = "" } = useParams();
  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  const [successOpen, setSuccessOpen] = useState(false);
  const { can } = usePermissions();
  const readOnly = !duplication && !can(PERMISSIONS.catalog.write);
  const [isPublishingDraft, setIsPublishingDraft] = useState(false);
  const [promoteError, setPromoteError] = useState<DraftErrorInfo | null>(null);

  const {
    wizardData,
    initialInitiativeIds,
    initialEditorial,
    isLoading,
    error,
    mutate,
  } = useCourseDetailV2({ corporateId, idCourse });

  const { submit: submitEdit, isSubmitting: isSubmittingEdit } =
    useEditCourseV2({
      corporateId,
      loId: Number(idCourse),
      initialInitiativeIds,
      initialEditorial,
    });

  const { submit: submitAdd, isSubmitting: isSubmittingAdd } =
    useSubmitCourseV2({ corporateId });

  const { saveDraft, isSavingDraft } = useSaveDraft({
    corporateId,
    type: "course",
  });
  const { promoteDraft } = usePromoteDraft({ corporateId });

  const isSubmitting = duplication
    ? isSubmittingAdd || isPublishingDraft
    : isSubmittingEdit;

  const initialData = useMemo<CourseWizardData | null>(() => {
    if (!wizardData) return null;
    if (!duplication) return wizardData;
    return {
      ...wizardData,
      title: `${wizardData.title || ""} - Copied`,
      schedule_publication: false,
      publish_date: null,
      schedule_obsolescence: false,
      obsolescent_date: null,
      selected_activities: wizardData.selected_activities.map((a) =>
        a.kind === "section"
          ? {
              ...a,
              locked: false,
              items: a.items.map((item) => ({ ...item, locked: false })),
            }
          : { ...a, locked: false },
      ),
      useful_links: wizardData.useful_links?.map((link) => ({
        ...link,
        id: null,
      })),
    };
  }, [wizardData, duplication]);

  const hasDraftChildren = (data: CourseWizardData) =>
    flattenSelectedLOItems(data.selected_activities).some(
      (a) => a.entity?.is_draft,
    );

  const publishWithDraftChildren = async (data: CourseWizardData) => {
    setIsPublishingDraft(true);
    try {
      const draft = await saveDraft(data);
      try {
        await promoteDraft(draft.id);
      } catch (err) {
        setPromoteError(extractPromoteError(err));
        throw err;
      }
    } finally {
      setIsPublishingDraft(false);
    }
  };

  const handleComplete = async (data: CourseWizardData) => {
    try {
      if (duplication) {
        if (hasDraftChildren(data)) {
          await publishWithDraftChildren(data);
        } else {
          await submitAdd(data);
        }
      } else {
        await submitEdit(data);
      }
      setSuccessOpen(true);
    } catch (err) {
      console.error(
        duplication
          ? "[wizard-course] duplicate failed"
          : "[wizard-course] edit failed",
        err,
      );
      throw err;
    }
  };

  const handleSaveDraft = async (data: CourseWizardData) => {
    try {
      await saveDraft(data);
      navigate(`${catalogUrl}?tab=all`);
    } catch (err) {
      console.error("[wizard-course] saveDraft failed", err);
      throw err;
    }
  };

  const handleDismissSuccess = () => {
    if (!duplication) void mutate();
    navigate(catalogUrl);
  };

  if (isLoading || (!initialData && !error)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !initialData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {t("edit-load-error.title") || "Impossibile caricare il corso"}
        </Typography>
        <Typography sx={{ fontSize: "13px" }}>
          {error || t("edit-load-error.description") || ""}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <CourseWizard
        initialData={initialData}
        onComplete={handleComplete}
        onSaveDraft={duplication ? handleSaveDraft : undefined}
        isSavingDraft={isSavingDraft}
        isSubmitting={isSubmitting}
        mode={duplication ? "add" : "edit"}
        readOnly={readOnly}
        duplication={duplication}
      />
      <LOOutcomeModalCst
        open={successOpen}
        entityType="course"
        mode={duplication ? "add" : "edit"}
        onDismiss={handleDismissSuccess}
      />
      <DraftPromoteErrorDialog
        error={promoteError}
        onClose={() => setPromoteError(null)}
      />
    </>
  );
};

export default EditCourse;
